import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Search, Pencil, Trash2, Phone, Mail, MapPin, Key, ShieldOff, ShieldCheck, Copy, Eye, EyeOff, Percent, Handshake } from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_FORM = {
  company_name: '', contact_name: '', email: '', phone: '',
  address: '', notes: '', active: true, commission_pct: 5,
};

export default function CommercialsPage() {
  const [commercials, setCommercials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [search, setSearch] = useState('');
  const [credentialsDialog, setCredentialsDialog] = useState(null);
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const { data } = await api.get(`/commercials${params}`);
      setCommercials(data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/commercials/${editing.id}`, form);
        toast.success('Commercial mis à jour');
        setDialogOpen(false);
      } else {
        const { data } = await api.post('/commercials', form);
        toast.success('Commercial créé');
        setDialogOpen(false);
        setCredentialsDialog({ company: data.company_name, login: data.login_email, password: data.generated_password });
      }
      setEditing(null);
      setForm(INITIAL_FORM);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({
      company_name: c.company_name || '',
      contact_name: c.contact_name || '',
      email: c.email || '',
      phone: c.phone || '',
      address: c.address || '',
      notes: c.notes || '',
      active: c.active !== false,
      commission_pct: c.commission_pct ?? 5,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce commercial et son compte ? Les projets et clients seront détachés.')) return;
    try { await api.delete(`/commercials/${id}`); toast.success('Supprimé'); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  const handleToggleAccess = async (c) => {
    try {
      const { data } = await api.put(`/commercials/${c.id}/toggle-access`);
      toast.success(data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) { toast.error('Mot de passe trop court'); return; }
    try {
      await api.put(`/commercials/${passwordDialog.id}/password`, { new_password: newPassword });
      toast.success('Mot de passe modifié');
      setPasswordDialog(null);
      setNewPassword('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié');
  };

  const handleNew = () => { setEditing(null); setForm(INITIAL_FORM); setDialogOpen(true); };

  return (
    <div className="space-y-6" data-testid="commercials-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Commerciaux</h1>
          <p className="text-sm text-zinc-500 mt-1">{commercials.length} commercial(aux)</p>
        </div>
        <Button data-testid="new-commercial-btn" onClick={handleNew} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
          <Plus className="w-4 h-4 mr-2" /> Nouveau commercial
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="commercial-search" placeholder="Rechercher..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm" />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm h-64 nexus-shimmer" />)}
        </div>
      ) : commercials.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Handshake className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
          Aucun commercial enregistré
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {commercials.map((c) => (
            <Card key={c.id} className="bg-zinc-900 border-zinc-800 rounded-sm nexus-card" data-testid={`commercial-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-semibold text-zinc-100">{c.company_name}</h3>
                    {c.contact_name && <p className="text-sm text-zinc-400">{c.contact_name}</p>}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-sm border ${c.account_active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                    {c.account_active ? 'Actif' : 'Révoqué'}
                  </span>
                </div>

                <div className="inline-flex items-center gap-1.5 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-sm mb-3">
                  <Percent className="w-3 h-3" /> {c.commission_pct}% sur marge nette
                </div>

                {c.login_email && (
                  <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-2.5 mb-3">
                    <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Identifiants</p>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-blue-400 font-mono truncate">{c.login_email}</span>
                      <button onClick={() => copyToClipboard(c.login_email)} className="text-zinc-600 hover:text-zinc-300 transition-colors">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    {c.plain_password && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-amber-400/80 font-mono">{c.plain_password}</span>
                        <button onClick={() => copyToClipboard(c.plain_password)} className="text-zinc-600 hover:text-zinc-300 transition-colors">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-1.5 text-xs text-zinc-500 mb-3">
                  {c.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" />{c.email}</div>}
                  {c.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{c.phone}</div>}
                  {c.address && <div className="flex items-center gap-2"><MapPin className="w-3 h-3" />{c.address}</div>}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3 pt-3 border-t border-zinc-800 text-xs">
                  <div>
                    <p className="text-zinc-600">Clients / Projets</p>
                    <p className="text-zinc-200 font-medium">{c.clients_count || 0} / {c.projects_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-zinc-600">CA généré</p>
                    <p className="text-zinc-200 font-medium">{formatCurrency(c.ca_generated || 0)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-600">Commissions dues</p>
                    <p className="text-amber-400 font-medium">{formatCurrency(c.commission_pending || 0)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-600">Commissions payées</p>
                    <p className="text-emerald-400 font-medium">{formatCurrency(c.commission_paid || 0)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-1 pt-2 border-t border-zinc-800/50">
                  <button data-testid={`edit-commercial-${c.id}`} onClick={() => handleEdit(c)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors" title="Modifier">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`password-commercial-${c.id}`} onClick={() => { setPasswordDialog(c); setNewPassword(''); }} className="p-1.5 text-zinc-500 hover:text-blue-400 transition-colors" title="Changer mot de passe">
                    <Key className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`toggle-commercial-${c.id}`} onClick={() => handleToggleAccess(c)}
                    className={`p-1.5 transition-colors ${c.account_active ? 'text-zinc-500 hover:text-rose-400' : 'text-zinc-500 hover:text-emerald-400'}`}
                    title={c.account_active ? "Révoquer l'accès" : "Réactiver l'accès"}>
                    {c.account_active ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                  </button>
                  <button data-testid={`delete-commercial-${c.id}`} onClick={() => handleDelete(c.id)} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors ml-auto" title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="commercial-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editing ? 'Modifier commercial' : 'Nouveau commercial'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {editing ? 'Modifiez les informations.' : 'Un identifiant et mot de passe seront générés automatiquement.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Société *</Label>
              <Input data-testid="form-commercial-company" value={form.company_name} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Contact</Label>
              <Input value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-zinc-400 text-sm">Adresse</Label>
              <Input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Commission (%) sur marge nette</Label>
              <Input data-testid="form-commercial-pct" type="number" step="0.1" value={form.commission_pct}
                onChange={(e) => setForm(f => ({ ...f, commission_pct: parseFloat(e.target.value) || 0 }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              <p className="text-[11px] text-zinc-600">Commission = {form.commission_pct}% × (Montant HT − Coût S/T) par commande</p>
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-zinc-400 text-sm">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-commercial-btn" onClick={handleSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              {editing ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials shown after creation */}
      <Dialog open={!!credentialsDialog} onOpenChange={(o) => !o && setCredentialsDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle>Identifiants générés — {credentialsDialog?.company}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Copiez et transmettez ces identifiants à votre commercial.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3">
              <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Email de connexion</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-400 font-mono">{credentialsDialog?.login}</span>
                <button onClick={() => copyToClipboard(credentialsDialog?.login)} className="text-zinc-500 hover:text-zinc-100"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3">
              <p className="text-xs text-zinc-600 uppercase tracking-wider mb-1">Mot de passe</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-amber-400 font-mono">{credentialsDialog?.password}</span>
                <button onClick={() => copyToClipboard(credentialsDialog?.password)} className="text-zinc-500 hover:text-zinc-100"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredentialsDialog(null)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={!!passwordDialog} onOpenChange={(o) => !o && setPasswordDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle>Changer le mot de passe — {passwordDialog?.company_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Label className="text-zinc-400 text-sm">Nouveau mot de passe</Label>
            <div className="relative">
              <Input data-testid="new-password-input" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm pr-9" />
              <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-100">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-password-btn" onClick={handleChangePassword} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
